/*
 * pdp-video-lightbox.js
 * One shared, accessible video lightbox for every converted PDP section.
 *
 * Trigger any element with:
 *   data-pdp-video-trigger
 *   data-pdp-video-kind="embed" | "file"   (default "embed")
 *   data-pdp-video-src="<youtube|vimeo url>" or "<uploaded mp4 url>"
 *
 * Handles: YouTube (watch / youtu.be / embed / live / shorts), Vimeo, uploaded mp4.
 * Behaviour: semi-transparent overlay, click-outside to close, ESC to close,
 *   mobile X (top-right of screen), body scroll lock, pause/teardown on close.
 *
 * Uses event delegation on document, so it covers all sections and survives
 * Shopify theme-editor section re-renders. Self-guards against double-init.
 */
(function () {
  if (window.__pdpVideoLightbox) return;
  window.__pdpVideoLightbox = true;

  var overlay, dialog, closeBtn, lastFocus;

  function build() {
    overlay = document.createElement('div');
    overlay.className = 'pdp-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');

    dialog = document.createElement('div');
    dialog.className = 'pdp-lightbox__dialog';

    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pdp-lightbox__close';
    closeBtn.setAttribute('aria-label', 'Close video');
    closeBtn.innerHTML = '&times;';

    overlay.appendChild(dialog);
    overlay.appendChild(closeBtn);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    closeBtn.addEventListener('click', close);
  }

  function toEmbed(src, muted) {
    if (!src) return null;
    var ytMute = muted ? '&mute=1' : '';
    var yt = src.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|live\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
    );
    if (yt) {
      return (
        'https://www.youtube.com/embed/' +
        yt[1] +
        '?autoplay=1' +
        ytMute +
        '&rel=0&playsinline=1'
      );
    }
    var vm = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) {
      return (
        'https://player.vimeo.com/video/' +
        vm[1] +
        '?autoplay=1' +
        (muted ? '&muted=1' : '')
      );
    }
    return null;
  }

  function open(opts) {
    if (!opts || !opts.src) return;
    if (!overlay) build();

    var markup;
    var isNative = opts.kind === 'file';
    // Default to muted autoplay (browser policy / review clips); callers can
    // opt out with data-pdp-video-muted="false" (e.g. a founder talking video).
    var muted = opts.muted !== false;
    if (isNative) {
      markup =
        '<video controls autoplay playsinline preload="metadata"' +
        (muted ? ' muted' : '') +
        ' src="' +
        encodeURI(opts.src) +
        '"></video>';
    } else {
      var embed = toEmbed(opts.src, muted);
      if (!embed) return;
      markup =
        '<iframe src="' +
        embed +
        '" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
    }

    dialog.classList.toggle('pdp-lightbox__dialog--native', isNative);
    dialog.innerHTML = markup;
    lastFocus = document.activeElement;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    closeBtn.focus();
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    dialog.innerHTML = ''; // stops playback (YouTube/Vimeo/video) on close
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  // Warm the connection to the video host on first hover/touch of any trigger,
  // so the player starts loading sooner once the user actually clicks.
  var warmed = false;
  function warm() {
    if (warmed) return;
    warmed = true;
    [
      'https://www.youtube.com',
      'https://www.youtube-nocookie.com',
      'https://i.ytimg.com',
      'https://i9.ytimg.com',
      'https://www.gstatic.com',
      'https://googleads.g.doubleclick.net',
      'https://static.doubleclick.net',
      'https://player.vimeo.com',
      'https://i.vimeocdn.com',
      'https://f.vimeocdn.com'
    ].forEach(function (host) {
      var link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = host;
      link.crossOrigin = '';
      document.head.appendChild(link);
    });
  }

  function maybeWarm(e) {
    var t = e.target.closest && e.target.closest('[data-pdp-video-trigger]');
    if (t) warm();
  }
  document.addEventListener('pointerover', maybeWarm, { passive: true });
  document.addEventListener('touchstart', maybeWarm, { passive: true });
  document.addEventListener('focusin', maybeWarm);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-pdp-video-trigger]');
    if (!trigger) return;
    e.preventDefault();
    warm();
    open({
      kind: trigger.getAttribute('data-pdp-video-kind') || 'embed',
      src: trigger.getAttribute('data-pdp-video-src'),
      muted: trigger.getAttribute('data-pdp-video-muted') !== 'false'
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay && overlay.classList.contains('is-open')) {
      close();
    }
  });

  window.pdpVideoLightbox = { open: open, close: close };
})();
